import React, { memo, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import HelpIcon from '@material-ui/icons/Help';
import { Modal } from '../../shared/ui';
import { lightTheme as theme } from '../../shared/theme';

const HELP_CONTENT_CONFIG = {
    VIEW_CLIENT_HOME: {
        titleKey: 'client_help_view_client_home_title',
        paragraphKeys: ['client_help_view_client_home_intro'],
        sections: [
            {
                listKeys: [
                    'client_help_view_client_home_item_quote',
                    'client_help_view_client_home_item_quotations',
                    'client_help_view_client_home_item_equipments',
                ],
            },
        ],
    },
    VIEW_CLIENT_QUOTATION: {
        titleKey: 'client_help_view_client_quotation_title',
        paragraphKeys: ['client_help_view_client_quotation_intro'],
        sections: [
            {
                headingKey: 'client_help_view_client_quotation_sections_heading',
                listKeys: [
                    'client_help_view_client_quotation_section_waiting',
                    'client_help_view_client_quotation_section_in_progress',
                    'client_help_view_client_quotation_section_finished',
                ],
            },
            {
                headingKey: 'client_help_view_client_quotation_cards_heading',
                listKeys: [
                    'client_help_view_client_quotation_card_estimate',
                    'client_help_view_client_quotation_card_budget',
                    'client_help_view_client_quotation_card_order',
                ],
            },
        ],
    },
    VIEW_CLIENT_ESTIMATE: {
        titleKey: 'client_help_view_client_estimate_title',
        paragraphKeys: ['client_help_view_client_estimate_intro'],
        sections: [
            {
                listKeys: [
                    'client_help_view_client_estimate_step_1',
                    'client_help_view_client_estimate_step_2',
                    'client_help_view_client_estimate_step_3',
                ],
            },
        ],
        closingKey: 'client_help_view_client_estimate_closing',
    },
    VIEW_CLIENT_EQUIPMENTS: {
        titleKey: 'client_help_view_client_equipments_title',
        paragraphKeys: ['client_help_view_client_equipments_intro'],
        sections: [
            {
                listKeys: [
                    'client_help_view_client_equipments_item_history',
                    'client_help_view_client_equipments_item_quote',
                    'client_help_view_client_equipments_item_photos',
                    'client_help_view_client_equipments_item_reports',
                ],
            },
        ],
    },
    VIEW_CLIENT_DETAILS: {
        titleKey: 'client_help_view_client_details_title',
        paragraphKeys: [
            'client_help_view_client_details_intro',
            'client_help_view_client_details_estimate',
            'client_help_view_client_details_budget',
        ],
    },
    VIEW_CLIENT_SHIPPING: {
        titleKey: 'client_help_view_client_shipping_title',
        paragraphKeys: ['client_help_view_client_shipping_intro'],
    },
    VIEW_CLIENT_EQUIPMENT_DETAILS: {
        titleKey: 'client_help_view_client_equipment_details_title',
        paragraphKeys: ['client_help_view_client_equipment_details_intro'],
    },
};

const FloatingHelpButton = styled.button`
    position: fixed;
    bottom: 100px;
    right: 24px;
    z-index: 1200;
    width: 56px;
    height: 56px;
    border: 0;
    border-radius: 999px;
    background: ${theme.colors.secondary};
    color: ${theme.colors.paper};
    box-shadow: ${theme.colors.shadowMd};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;

    &:hover {
        transform: translateY(-1px);
        box-shadow: ${theme.colors.shadowLg};
        filter: brightness(0.96);
    }

    &:focus {
        outline: 2px solid ${theme.colors.action};
        outline-offset: 2px;
    }

    @media (max-width: 768px) {
        width: 52px;
        height: 52px;
        bottom: 88px;
        right: 16px;
    }
`;

const StyledHelpIcon = styled(HelpIcon)`
    font-size: 1.8rem;
`;

const ModalBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const Paragraph = styled.p`
    margin: 0;
    color: ${theme.colors.textSecondary};
    line-height: 1.55;
`;

const Section = styled.section`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const SectionHeading = styled.h4`
    margin: 0;
    font-size: 0.92rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${theme.colors.textMuted};
`;

const List = styled.ul`
    margin: 0;
    padding-left: 18px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    color: ${theme.colors.textSecondary};
`;

const ListItem = styled.li`
    line-height: 1.45;
`;

const mapKeys = (keys = [], clientLanguage) =>
    keys
        .map((key) => clientLanguage[key])
        .filter(Boolean);

const buildHelpContent = (view, clientLanguage) => {
    const config = HELP_CONTENT_CONFIG[view];
    if (!config) return null;

    return {
        title: clientLanguage[config.titleKey],
        paragraphs: mapKeys(config.paragraphKeys, clientLanguage),
        sections: (config.sections || [])
            .map((section) => ({
                heading: section.headingKey ? clientLanguage[section.headingKey] : '',
                items: mapKeys(section.listKeys, clientLanguage),
            }))
            .filter((section) => section.heading || section.items.length > 0),
        closing: config.closingKey ? clientLanguage[config.closingKey] : '',
    };
};

function HelpButton({ view, currentLanguage }) {
    const [isOpen, setIsOpen] = useState(false);
    const clientLanguage = currentLanguage.bepiService.client;
    const sharedLanguage = currentLanguage.bepiService.shared;

    const content = useMemo(
        () => buildHelpContent(view, clientLanguage),
        [view, clientLanguage]
    );

    const modalTitle = clientLanguage.client_help_modal_title;
    const closeLabel = sharedLanguage.shared_estimate_respond_close_btn;
    const emptyMessage = clientLanguage.client_help_modal_not_available;

    return (
        <>
            <FloatingHelpButton
                type='button'
                onClick={() => setIsOpen(true)}
                aria-label={modalTitle}
            >
                <StyledHelpIcon />
            </FloatingHelpButton>

            <Modal
                isOpen={isOpen}
                type='info'
                align='left'
                maxWidth='760px'
                title={content?.title || modalTitle}
                onCancel={() => setIsOpen(false)}
                cancelText={closeLabel}
                showIcon={false}
            >
                {content ? (
                    <ModalBody>
                        {content.paragraphs.map((paragraph) => (
                            <Paragraph key={paragraph}>{paragraph}</Paragraph>
                        ))}

                        {content.sections.map((section) => (
                            <Section key={`${section.heading}-${section.items.join('|')}`}>
                                {section.heading ? <SectionHeading>{section.heading}</SectionHeading> : null}
                                {section.items.length ? (
                                    <List>
                                        {section.items.map((item) => (
                                            <ListItem key={item}>{item}</ListItem>
                                        ))}
                                    </List>
                                ) : null}
                            </Section>
                        ))}

                        {content.closing ? <Paragraph>{content.closing}</Paragraph> : null}
                    </ModalBody>
                ) : (
                    <Paragraph>{emptyMessage}</Paragraph>
                )}
            </Modal>
        </>
    );
}

HelpButton.propTypes = {
    view: PropTypes.string.isRequired,
    currentLanguage: PropTypes.object.isRequired,
};

export default memo(HelpButton);
